import React from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import Card from './Card';
import Button from './Button';

/** The grid every list of cards uses (Staff's), so all tabs line up. */
export const CARD_GRID = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6';

/** A small label chip, as on the staff cards. `tone`: slate | teal | indigo | amber | rose. */
export function Chip({ children, tone = 'slate', title }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-500', teal: 'bg-teal-50 text-teal-700',
    indigo: 'bg-indigo-50 text-indigo-700', amber: 'bg-amber-100 text-amber-800',
    rose: 'bg-rose-50 text-rose-600',
  };
  return (
    <span title={title} className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

/** A captioned section inside a card body (e.g. "Qualifications"). */
export function CardSection({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5 ml-1">
        {Icon && <Icon className="w-3 h-3 text-slate-300" />}
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * One item of a list, as a card (Oct 2026: every list -- staff, clinics, skills,
 * shift types, positions, on-call roles, clinic groups -- is cards in CARD_GRID).
 * Edit and delete appear on hover, as on the staff cards; edit opens the item's
 * panel. `deleteDisabledReason` greys delete out and says why.
 */
export default function ItemCard({ title, accent, badges, onEdit, onDelete, deleteDisabledReason,
                                   footerLeft, footerRight, children, className = '' }) {
  const name = typeof title === 'string' ? title : undefined;
  return (
    <Card padding="p-0" className={`flex flex-col group overflow-hidden animate-in fade-in zoom-in-95 duration-300 ${className}`}>
      {accent && <div className="h-1.5 w-full" style={{ backgroundColor: accent }} />}
      <div className="p-5 flex-1 relative">
        <div className={`flex justify-between items-start ${children ? 'mb-5' : ''}`}>
          <div className="min-w-0">
            <h4 className="font-bold text-slate-900 tracking-tight truncate">{title}</h4>
            {badges && <div className="flex flex-wrap items-center gap-1.5 mt-1">{badges}</div>}
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            {onEdit && <Button variant="ghost" size="xs" onClick={onEdit} icon={Edit2}
              aria-label={name ? `Edit ${name}` : 'Edit'} title="Edit" />}
            {onDelete && <Button variant="ghost" size="xs" onClick={onDelete} icon={Trash2}
              disabled={Boolean(deleteDisabledReason)}
              aria-label={name ? `Remove ${name}` : 'Remove'} title={deleteDisabledReason || 'Remove'}
              className="hover:bg-[var(--primary)]/10 hover:text-[var(--primary)]" />}
          </div>
        </div>
        {children && <div className="space-y-5">{children}</div>}
      </div>
      {(footerLeft || footerRight) && (
        <div className="px-5 py-3 border-t border-slate-50 bg-slate-50/30 rounded-b-[20px] flex items-center justify-between">
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{footerLeft}</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{footerRight}</span>
        </div>
      )}
    </Card>
  );
}
